/**
 * Workshop AgendaFrame — day agenda with time gutter + event blocks.
 *
 * Native-source note: no official OSS component exists for this
 * calendar-agenda layout, so rows are recreated pinned to spec (muted
 * time gutter, bg-elevated/60 blocks, current event with accent-tinted
 * fill + accent left edge). Title/detail text styles copied from
 * taskboard list-view-row (text-appkit-caption title, text-canvas-xs
 * muted detail). Status pill uses the production status tint
 * (bg-success-muted / text-success). Denker glass outside.
 * No FrameHeader/FrameFooter — hover pill only. Item clicks report ids
 * via callback and never execute anything.
 */

import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  Spinner,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

interface AgendaItem {
  id: string;
  time: string;
  title: string;
  detail?: string;
  current?: boolean;
}

interface AgendaFrameProps {
  date?: string;
  statusPill?: string;
  items?: AgendaItem[];
  status?: FrameStatus;
  onItemClick?: (id: string) => void;
  className?: string;
  "data-testid"?: string;
}

const MOCK_DATE = "Wednesday, Oct 22";

const MOCK_ITEMS: AgendaItem[] = [
  {
    id: "a1",
    time: "12:00",
    title: "Lunch with Priya",
    detail: "Kreuzberg · 1h",
  },
  {
    id: "a2",
    time: "14:30",
    title: "Keynote — Designing with AI",
    detail: "Config, Berlin · Main stage",
    current: true,
  },
  {
    id: "a3",
    time: "16:00",
    title: "Design systems crit",
    detail: "Studio call · 45 min",
  },
];

export function AgendaFrame({
  date = MOCK_DATE,
  statusPill = "Focus held",
  items = MOCK_ITEMS,
  status = "done",
  onItemClick,
  className,
  "data-testid": testId = "agenda-frame",
}: AgendaFrameProps) {
  const isEmpty = items.length === 0;

  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex w-[340px] flex-col overflow-hidden rounded-frame",
        className,
      )}
      data-testid={testId}
      {...surfaceRoleAttributes("frame")}
    >
      <div
        className="nodrag flex flex-1 flex-col gap-2.5 px-3 py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        {status === "error" ? (
          <div
            className="flex items-center gap-2 rounded-preview bg-danger-muted px-3 py-4"
            data-testid={`${testId}-error`}
            role="alert"
          >
            <Icons.CircleAlert
              className="h-3.5 w-3.5 shrink-0 text-status-error"
              aria-hidden="true"
            />
            <span className="text-canvas-sm text-secondary">
              Couldn&apos;t load today&apos;s agenda. Try again.
            </span>
          </div>
        ) : status === "streaming" ? (
          <div data-testid={`${testId}-loading`} aria-label="Loading agenda">
            <div className="flex items-center gap-2">
              <Spinner className="h-3 w-3" />
              <span className="text-canvas-xs text-muted">
                Pulling today&apos;s agenda…
              </span>
            </div>
            <div className="mt-2 flex flex-col gap-1.5" aria-hidden="true">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5"
                >
                  <div className="h-2.5 w-8 animate-pulse rounded bg-elevated/60" />
                  <div className="h-11 flex-1 animate-pulse rounded-control bg-elevated/60" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div
              className="flex items-center justify-between gap-2"
              data-testid={`${testId}-header`}
            >
              <span
                className="truncate text-canvas-sm font-semibold text-primary"
                data-testid={`${testId}-date`}
              >
                {date}
              </span>
              {statusPill && (
                <span
                  className="shrink-0 rounded-pill bg-success-muted px-2 py-0.5 text-canvas-xs font-medium text-success"
                  data-testid={`${testId}-status-pill`}
                >
                  {statusPill}
                </span>
              )}
            </div>

            {isEmpty ? (
              <div
                className="flex flex-col items-center gap-1.5 rounded-preview bg-elevated/60 px-3 py-6 text-center"
                data-testid={`${testId}-empty`}
              >
                <Icons.Calendar
                  className="h-4 w-4 text-muted"
                  aria-hidden="true"
                />
                <p className="text-canvas-sm italic text-muted">
                  Nothing scheduled today
                </p>
              </div>
            ) : (
              <div
                className="flex flex-col gap-1.5"
                data-testid={`${testId}-items`}
              >
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-stretch gap-2.5"
                    data-testid={`${testId}-item-${item.id}`}
                  >
                    <span className="w-10 shrink-0 pt-2 text-right text-canvas-xs tabular-nums text-muted">
                      {item.time}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemClick?.(item.id);
                      }}
                      data-testid={`${testId}-item-${item.id}-open`}
                      className={cn(
                        "min-w-0 flex-1 rounded-control border-l-2 px-2.5 py-2 text-left transition-colors hover:bg-glass-stroke-faint/20",
                        item.current
                          ? "border-l-accent bg-accent/18"
                          : "border-l-transparent bg-elevated/60",
                      )}
                    >
                      <span className="block truncate text-appkit-caption font-semibold text-primary">
                        {item.title}
                      </span>
                      {item.detail && (
                        <span className="mt-0.5 block truncate text-canvas-xs text-muted">
                          {item.detail}
                        </span>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}

export type { AgendaFrameProps, AgendaItem };
